/**
 * World Utilities - Shared functions for the world navigation system
 * 
 * Centralizes icon mapping and other utility functions used across
 * WorldMapPage, MidgardRegionalMap, SettlementPage, LocationPage.
 */

import { Home, Store, Beer, Trees, Building, Mountain, Waves, Route, MapPin, Users, Skull } from 'lucide-react';
import { LOCATION_TYPES } from './worldData';

/**
 * Get the Lucide icon component for a given location type
 * @param {string} type - Location type from LOCATION_TYPES or custom types
 * @returns {React.Component} Lucide icon component
 */
export const getLocationIcon = (type) => {
  switch (type) {
    case LOCATION_TYPES.TAVERN:
      return Beer;
    case LOCATION_TYPES.SHOP:
      return Store;
    case LOCATION_TYPES.WILDERNESS:
      return Trees;
    case LOCATION_TYPES.BUILDING:
      return Building;
    case LOCATION_TYPES.MOUNTAIN:
      return Mountain;
    case LOCATION_TYPES.RIVER:
      return Waves;
    case LOCATION_TYPES.ROAD:
      return Route;
    case LOCATION_TYPES.DUNGEON:
      return Skull;
    case LOCATION_TYPES.TOWN:
    case LOCATION_TYPES.VILLAGE:
    case LOCATION_TYPES.CITY:
      return Building;
    case LOCATION_TYPES.PLAYER_HOUSE:
    case 'residential':
      return Home;
    case 'gathering':
      return Users;
    default:
      return MapPin;
  }
};

/**
 * Get a display-friendly label for a location type
 * @param {string} type - Location type
 * @returns {string} Human-readable label
 */
export const getLocationTypeLabel = (type) => {
  if (typeof type !== 'string') return 'Location';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Get background gradient class for a location based on its type
 * @param {string} type - Location type
 * @param {object} theme - Settlement theme object with bgGradient
 * @returns {string} Tailwind gradient classes
 */
export const getLocationBackground = (type, theme) => {
  switch (type) {
    case LOCATION_TYPES.TAVERN:
      return 'from-orange-950/80 via-amber-900/60 to-stone-900/80';
    case LOCATION_TYPES.SHOP:
      return 'from-amber-900/80 via-yellow-900/60 to-stone-900/80';
    case LOCATION_TYPES.WILDERNESS:
      return 'from-green-950/80 via-emerald-900/60 to-stone-900/80';
    case LOCATION_TYPES.ROAD:
      return 'from-stone-800/80 via-amber-900/60 to-stone-900/80';
    case LOCATION_TYPES.MOUNTAIN:
      return 'from-slate-800/80 via-stone-700/60 to-gray-900/80';
    case LOCATION_TYPES.RIVER:
      return 'from-blue-950/80 via-cyan-900/60 to-slate-900/80';
    case 'gathering':
      return 'from-amber-900/80 via-orange-900/60 to-stone-900/80';
    case 'residential':
      return 'from-stone-800/80 via-amber-950/60 to-stone-900/80';
    default:
      return theme?.bgGradient || 'from-stone-900 via-stone-800 to-stone-900';
  }
};

/**
 * Build breadcrumb path for navigation display
 * @param {object} params - { realm, settlement, location }
 * @returns {Array} Array of { label, path } objects
 */
export const buildBreadcrumb = ({ realm, settlement, location }) => {
  const crumbs = [];
  
  if (realm) {
    crumbs.push({ label: realm.name, path: `/world/${realm.id}` });
  }
  
  if (settlement) {
    crumbs.push({ 
      label: settlement.name, 
      path: `/world/${realm?.id || 'midgard'}/settlement/${settlement.id}` 
    });
  }
  
  if (location) {
    crumbs.push({ 
      label: location.name, 
      path: null // Current location, no link
    });
  }
  
  return crumbs;
};

/**
 * Get parent path for back navigation
 * Handles the hierarchy: location → settlement → region → world
 * @param {string} currentPath - Current URL path
 * @returns {string} Parent path
 */
export const getParentPath = (currentPath) => {
  const segments = currentPath.split('/').filter(Boolean);
  
  // /world/midgard/settlement/x/location/y → /world/midgard/settlement/x
  if (segments.includes('location')) {
    const locationIndex = segments.indexOf('location');
    return '/' + segments.slice(0, locationIndex).join('/');
  }
  
  // /world/midgard/settlement/x → /world/midgard
  if (segments.includes('settlement')) {
    const settlementIndex = segments.indexOf('settlement');
    return '/' + segments.slice(0, settlementIndex).join('/');
  }
  
  // /world/midgard → /world
  if (segments.length > 1 && segments[0] === 'world') {
    return '/world';
  }
  
  // Default fallback
  return '/world';
};
